import {Attachment, AttachmentTypeEnum} from '../types/appointments';
import {
    getAttachmentsMetaData,
    getAttachmentUrlOrDownload,
    removeAttachment,
    uploadAttachments,
} from '../store/appointmentAttachmentsSlice';
import {useAppDispatch} from './customReduxHooks';
import {getFileIcon, toBase64} from '../utils/file';
import {useUserCheck} from './useUserCheck';
import filesDownloadProvider from '../services/filesDownloadProvider';

export const useHandleRemoveAsync = () => {
    const dispatch = useAppDispatch();
    return async (appointmentId: string, attachmentId: string) => {
        await dispatch(
            removeAttachment({
                appointmentId,
                attachmentId: attachmentId,
            }),
        );
        dispatch(getAttachmentsMetaData({appointmentId, fetchSilently: true}));
    };
};

export const useHandleUploadAsync = () => {
    const dispatch = useAppDispatch();
    return async (
        appointmentId: string,
        files: File[],
        attachmentType?: AttachmentTypeEnum,
    ) => {
        const filesUploadOngoing = files.map(async file => {
            const formData = new FormData();
            formData.append('file', file, file.name);
            formData.append('isPrescription', 'false');
            formData.append('contentType', file.type);
            if (attachmentType !== undefined) {
                formData.append('attachmentType', attachmentType);
            }
            return dispatch(
                uploadAttachments({
                    appointmentId,
                    uploadData: formData
                }),
            );
        });
        await Promise.all(filesUploadOngoing);
        dispatch(getAttachmentsMetaData({appointmentId, fetchSilently: true}));
    };
};

export const useOpenOrDownloadAsync = () => {
    const dispatch = useAppDispatch();
    return async (appointmentId: string, attachmentId: string) => {
        const attachment: Attachment | string = (
            await dispatch(
                getAttachmentUrlOrDownload({
                    appointmentId: appointmentId,
                    attachmentId: attachmentId,
                }),
            )
        ).payload as Attachment | string;

        // If endpoint returns url - open attachment in a new tab
        if(typeof attachment === 'string') {
            window.open(attachment, '_blank');   
            return;
        }

        // If endpoint returns metadata - download attachment
        // Workaround for downloading pdf files on ios
        let contentType = attachment.contentType;
        if (attachment.contentType === 'application/pdf')
            contentType = 'application/jpg';
        else contentType = attachment.contentType;

        filesDownloadProvider.downloadBase64File(
            contentType,
            attachment.content,
            attachment.fileName,
        );
    };
};

export const useGetFileUploadViewModelItems = (
    attachmentsMetaData: Attachment[],
    appointmentId: string,
) => {
    const openOrDownloadFileAsync = useOpenOrDownloadAsync();
    const removeFileAsync = useHandleRemoveAsync();
    const isLoggedUser = useUserCheck();
    return attachmentsMetaData.map(attachmentMetaData => ({
        fileItemKey: attachmentMetaData.id,
        fileItemClickHandler: () =>
            openOrDownloadFileAsync(appointmentId, `${attachmentMetaData.id}`),
        fileName: attachmentMetaData.fileName,
        fileIcon: getFileIcon(
            attachmentMetaData.contentType,
            attachmentMetaData.thumbnail,
        ),
        fileOwner: attachmentMetaData.person,
        fileDateCreated:
            attachmentMetaData.uploadDate.toFormat("dd'.'MM'.'yyyy"),
        fileTimeCreated: attachmentMetaData.uploadDate.toFormat("HH':'mm"),
        removeFileEnabled: isLoggedUser(attachmentMetaData.personId),
        removeFileHandler: () =>
            removeFileAsync(appointmentId, `${attachmentMetaData.id}`),
    }));
};
